import styles from '../../style.module.scss';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import {
    Modal,
    ModalHeader,
    ModalContent,
    ModalFooter,
    Button,
    FormFieldInputNew,
    Layout,
    useForm,
    extractErrorMessage
} from '@netapp/bxp-design-system-react';
import { apiClient } from 'utils/clients';
import { notificationSettings as i18nNamespace } from '../../../../i18n/namespace';

const TestEmailConfigDialog = React.memo(({ setIsTestEmailConfigOpen, emailServerConfigDetailsForm, onSuccess }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(i18nNamespace);
    const [isTestEmailConfigInProgress, setIsTestEmailConfigInProgress] = useState(false);
    const [error, setError] = useState(null);
    const selectedOrgId = useSelector(state => state?.tenancy?.selectedOrgId);

    const validateTestEmailConfigSchema = yup.object().shape({
        address: yup
            .string()
            .required(t('recipient_email_required'))
            .email(t('recipient_email_invalid')),
        subject: yup
            .string()
            .required(t('smtp_subject_required')),
    });

    const form = useForm({
        address: '',
        subject: ''
    }, validateTestEmailConfigSchema);

    const handleTestEmailServerConfig = async () => {
        setError(null);
        setIsTestEmailConfigInProgress(true);
        if (!form.isValid) {
            return;
        }
        let payload = { ...emailServerConfigDetailsForm.formState };
        payload.tlsCertificate = payload.tlsCertificate ? payload.tlsCertificate.contents : '';
        payload.port = Number(payload.port);
        delete payload.connectionSecurity;
        delete payload.id;
        payload.address = form.formState.address.trim();
        payload.subject = form.formState.subject;
        if (payload.username === '' || payload.password === '') {
            delete payload.username;
            delete payload.password;
        }
        try {
            const response = await apiClient.post(`${process.env.REACT_APP_OCCM_ENDPOINT}organizations/${selectedOrgId}/email-config/test-config`, payload);
            if (response) {
                dispatch({
                    type: 'NOTIFICATIONS:SUCCESS',
                    payload: {
                        message: t('email_test_success', i18nNamespace),
                        key: 'POST-TEST-EMAIL-CONFIG',
                        showGlobal: true
                    }
                });
                if (onSuccess) onSuccess();
            }
            setIsTestEmailConfigOpen(false);
        } catch (error) {
            console.error(error);
            setError(extractErrorMessage(error, 'detail'));
        } finally {
            setIsTestEmailConfigInProgress(false);
        }
    };

    return (
        <>
            <Modal>
                <ModalHeader>{t('send_test_email')}</ModalHeader>
                <ModalContent>
                    <div className={styles.width100}>
                        <Layout.Grid rowGap='md'>
                            <Layout.GridItem lg={6} md={6}>
                                <FormFieldInputNew
                                    form={form}
                                    label={t('recipient_email')}
                                    name='address'
                                    showError={(form.dirty.address || form.submissionAttempted) && form.formErrors.address}
                                />
                            </Layout.GridItem>
                            <Layout.GridItem lg={6} md={6}>
                                <FormFieldInputNew
                                    form={form}
                                    label={t('subject')}
                                    name='subject'
                                    showError={(form.dirty.subject || form.submissionAttempted) && form.formErrors.subject}
                                />
                            </Layout.GridItem>
                        </Layout.Grid>
                    </div>
                </ModalContent>
                <ModalFooter error={error}>
                    <Button
                        className={styles.width152}
                        isSubmitting={isTestEmailConfigInProgress}
                        onClick={form.handleFormSubmit(handleTestEmailServerConfig)}
                        disabled={!form.isValid}
                    >
                        {t('send')}
                    </Button>
                    <Button
                        color='secondary'
                        disabled={isTestEmailConfigInProgress}
                        className={styles.width152}
                        onClick={() => setIsTestEmailConfigOpen(false)}
                    >
                        {t('cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
});

export default TestEmailConfigDialog;